import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { Home, ShoppingBag, Users, BarChart3, Settings } from 'lucide-react';
import AppContext from '../context/AppContext';
import { useNavigation } from '../context/NavigationContext';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const { setupTelegramBackButton, ontg, isSplashVisible } = useContext(AppContext);
  
  // Déterminer l'onglet actif en fonction de l'URL et gérer le bouton retour
  useEffect(() => {
    const path = location.pathname;
    console.log("Layout - Chemin actuel:", path);
    
    // Identifier les routes principales (celles qui sont directement dans la bottom bar)
    const mainRoutes = [
      '/', 
      '/IndexEspace', 
      '/ProductsList', 
      '/GroupeListe', 
      '/Statistics', 
      '/PaymentsSheetsIndex'
    ];
    
    // Vérifier si nous sommes sur une route principale
    const isMainRoute = mainRoutes.includes(path);
    
    // Sous-routes qui ont la bottom bar
    const isSubRoute = path.startsWith('/ProductCard/') || path.startsWith('/GroupeDetail/');
    
    // Ajouter des routes des réglages
    const isSettingsRoute = path.includes('/ModifierKey') || 
                           path.includes('/PaymentSheet') || 
                           path.includes('/AjouterMethode') || 
                           path.includes('/ModifierKeyIndex') || 
                           path.includes('/ActivationMethode') || 
                           path.includes('/SuccesBottomSheetIndex') ||
                           path.includes('/CustomPaymentConfig') ||
                           path.includes('/ConfigurerPaiement');
    
    console.log("Layout - Types de route:", { isMainRoute, isSubRoute, isSettingsRoute });
    
    // Gestion du bouton retour pour les sous-routes
    if (isSubRoute) {
      // Déterminer la route parente pour le bouton retour
      let parentRoute = '/IndexEspace'; // Par défaut
      
      if (path.startsWith('/ProductCard/')) {
        parentRoute = '/ProductsList';
      } else if (path.startsWith('/GroupeDetail/')) {
        parentRoute = '/GroupeListe';
      }
      
      console.log("Layout - Route parente pour sous-route:", parentRoute);
      
      // Nous n'utilisons plus Telegram WebApp
      // Utilisez simplement la navigation React Router native
    } else if (isMainRoute) {
      // Sur une route principale, pas de bouton retour nécessaire
      console.log("Layout - Route principale, pas de bouton retour");
    }
    
    // Gestion de la navigation pour le bouton back du navigateur (non Telegram)
    if (!isMainRoute && !isSubRoute) {
      // Ce code ne s'exécutera pas pour les routes couvertes par Layout
      let parentRoute = '/IndexEspace'; // Par défaut
      
      if (path.includes('/ProductCard')) {
        parentRoute = '/ProductsList';
      } else if (path.includes('/GroupeDetail')) {
        parentRoute = '/GroupeListe';
      } else if (isSettingsRoute) {
        // Pour les sous-routes des réglages, rediriger vers PaymentsSheetsIndex
        parentRoute = '/PaymentsSheetsIndex';
      }
      
      console.log("Layout - Route parente pour navigation:", parentRoute);
    }
    
    // Définir l'onglet actif
    if (path === '/IndexEspace' || path === '/') {
      setActiveTab('dashboard');
    } else if (path.includes('/ProductsList') || path.includes('/ProductCard') || path.includes('/ajouterProduit')) {
      setActiveTab('products');
    } else if (path.includes('/GroupeListe') || path.includes('/GroupeDetail')) {
      setActiveTab('groups');
    } else if (path.includes('/Statistics')) {
      setActiveTab('stats');
    } else if (path.includes('/PaymentsSheetsIndex') || path.includes('/Settings') || isSettingsRoute) {
      setActiveTab('settings');
    }
    
    console.log("Layout - Onglet actif défini:", activeTab);
  }, [location, navigate, ontg, setupTelegramBackButton]);
  
  // Nous n'utilisons plus Telegram WebApp
  // Pas besoin de nettoyer le bouton retour
  
  // Utiliser le navigateTo du contexte de navigation
  const { navigateTo } = useNavigation();
  
  // Définir le style de base pour le safe area (espacement pour éviter de masquer le contenu)
  const safeAreaStyle = { paddingBottom: '56px' };
  
  // Vérifier si nous sommes sur une page de réglages pour cacher la bottom bar standard
  // Ne masquons pas la barre pour PaymentsSheetsIndex car c'est une route principale de la navigation
  const isSettingsPage = location.pathname.includes('ModifierKey') || 
                       location.pathname.includes('AjouterMethode');
  
  console.log("Chemin actuel:", location.pathname, "Est page réglages:", isSettingsPage);
                       
  return (
    <div className="min-h-screen bg-gray-50" style={safeAreaStyle}>
      {/* Contenu principal - utilise Outlet de React Router pour afficher les routes enfants */}
      <main className="pb-16">
        <Outlet />
      </main>
      
      {/* Bottom Navigation - Ne s'affiche pas sur les pages de réglages qui ont déjà leur propre barre */}
      {!isSettingsPage && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-[9000] shadow-lg">
          {/* Indicateur de navigation actif - positionné dynamiquement selon l'onglet actif */}
          <div className="absolute -top-1 left-0 right-0 flex justify-evenly">
            <div 
              className={`h-1 w-16 bg-blue-600 rounded-full transition-transform duration-300 transform ${
                activeTab === 'dashboard' ? 'translate-x-[-120%]' : 
                activeTab === 'products' ? 'translate-x-[-40%]' : 
                activeTab === 'groups' ? 'translate-x-[40%]' : 
                'translate-x-[120%]'
              }`}
            ></div>
          </div>
          <div className="max-w-lg mx-auto flex justify-around px-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <button 
            onClick={() => navigateTo('/IndexEspace')}
            className={`flex flex-col items-center py-3 px-4 ${activeTab === 'dashboard' ? 'text-blue-600' : 'text-gray-500'}`}
            aria-label="Accueil"
          >
            <Home className="w-6 h-6" />
            <span className="text-xs mt-2">Accueil</span>
          </button>
          
          <button 
            onClick={() => navigateTo('/ProductsList')}
            className={`flex flex-col items-center py-3 px-4 ${activeTab === 'products' ? 'text-blue-600' : 'text-gray-500'}`}
            aria-label="Produits"
          >
            <ShoppingBag className="w-6 h-6" />
            <span className="text-xs mt-2">Produits</span>
          </button>
          
          <button 
            onClick={() => navigateTo('/GroupeListe')}
            className={`flex flex-col items-center py-3 px-4 ${activeTab === 'groups' ? 'text-blue-600' : 'text-gray-500'}`}
            aria-label="Groupes"
          >
            <Users className="w-6 h-6" />
            <span className="text-xs mt-2">Groupes</span>
          </button>
          
          {/* Menu Statistics commenté comme demandé 
          <button 
            onClick={() => navigateTo('/Statistics')}
            className={`flex flex-col items-center py-3 px-4 ${activeTab === 'stats' ? 'text-blue-600' : 'text-gray-500'}`}
            aria-label="Statistiques"
          >
            <BarChart3 className="w-6 h-6" />
            <span className="text-xs mt-2">Stats</span>
          </button>
          */}
          
          <button 
            onClick={() => navigateTo('/PaymentsSheetsIndex')}
            className={`flex flex-col items-center py-3 px-4 ${activeTab === 'settings' ? 'text-blue-600' : 'text-gray-500'}`}
            aria-label="Réglages"
          >
            <Settings className="w-6 h-6" />
            <span className="text-xs mt-2">Réglages</span>
          </button>
        </div>
      </nav>
      )}
      
      {/* Styles globaux */}
      <style jsx="true">{`
        @media (max-width: 768px) {
          nav button:active {
            opacity: 0.7;
          }
        }
      `}</style>
      
      {/* Surcharge pour assurer la visibilité de la barre de navigation */}
      <style>
        {`
          .fixed.bottom-0 {
            position: fixed !important;
            z-index: 9999 !important;
            bottom: 0 !important;
            left: 0 !important;
            right: 0 !important;
            display: flex !important;
          }
        `}
      </style>
    </div>
  );
};

export default Layout;